<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('serviceSubscription.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('serviceSubscription.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div>
            <ts-button color="info" outline @click.prevent="showForm">{{
              $t('serviceSubscription.subscription')
            }}</ts-button>
            <ts-button
              outline
              class="ml-2"
              @click.prevent="$router.push({ name: 'service-unsubscription' })"
            >
              <i class="fa fa-history mr-1"></i>
              {{ $t('serviceSubscription.viewUnsubscription') }}
            </ts-button>
          </div>
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'student.full_name_en'
          }"
        >
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td>{{ record.student.card_number }}</td>
            <td>{{ record.fee_type.fee_type_name }}</td>
            <td>{{ record.fee_setup.fee_name_en }}</td>
            <td>{{ record.subs_comment }}</td>
            <td>{{ getActionUserName(record) }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.action_date }}</td>
            <td class="text-center">
              <a
                href="#"
                :disabled="record._deleting"
                @click.prevent="showUnsubscribeModal(record)"
              >
                <i
                  class="fas fa-circle-notch spin ml-2"
                  v-if="record._deleting"
                ></i>
                <i class="fa fa-trash ml-2 text-red" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <a-drawer
        :title="$t('filter')"
        placement="right"
        :closable="false"
        :visible="visible"
        @close="() => (visible = false)"
        :zIndex="1020"
        width="300px"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <a-select
                style="width: 257px"
                v-model="branch_id"
                :options="branchs"
                showSearch
                mode="multiple"
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :maxTagCount="1"
                :maxTagTextLength="17"
                :placeholder="$t('all')"
              >
              </a-select>
            </div>
          </div>
        </div>
      </a-drawer>
    </ts-card>
    <a-modal
      v-model="unsubscribeModal.visible"
      :title="$t('serviceSubscription.unsubscribeTitle')"
      :ok-text="$t('yes')"
      :cancel-text="$t('no')"
      :confirm-loading="unsubscribeModal.loading"
      @ok="deleteRecord"
    >
      <div class="form-group">
        <label
          >{{ $t('serviceSubscription.unsubscribeDate') }}
          <span class="text-danger">*</span></label
        >
        <a-date-picker
          v-model="unsubscribeModal.unsubsDate"
          :style="{ width: '100%' }"
          format="DD-MM-YYYY"
          valueFormat="DD-MM-YYYY"
          :allowClear="false"
          :class="{ 'is-invalid': unsubscribeModal.errors.unsubsDate }"
        />
        <div
          class="invalid-feedback d-block"
          v-if="unsubscribeModal.errors.unsubsDate"
        >
          {{ unsubscribeModal.errors.unsubsDate }}
        </div>
      </div>
      <div class="form-group">
        <label
          >{{ $t('serviceSubscription.unsubscribeReason') }}
          <span class="text-danger">*</span></label
        >
        <textarea
          v-model.trim="unsubscribeModal.unsubsReason"
          class="form-control"
          :class="{ 'is-invalid': unsubscribeModal.errors.unsubsReason }"
          rows="3"
        ></textarea>
        <div
          class="invalid-feedback d-block"
          v-if="unsubscribeModal.errors.unsubsReason"
        >
          {{ unsubscribeModal.errors.unsubsReason }}
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import FormComponent from './form'
import { debounce } from 'lodash'
import moment from 'moment'
export default {
  name: 'serviceSubscription',
  data() {
    return {
      loading: false,
      visible: false,
      branchs: [],
      unsubscribeModal: {
        visible: false,
        loading: false,
        record: null,
        unsubsDate: moment().format('DD-MM-YYYY'),
        unsubsReason: '',
        errors: {}
      }
    }
  },
  created() {
    this.branch_id = [this.$store.state.authUser.default_branch_id]
    this.fetchData()
    this.getBranch()
  },
  computed: {
    ...mapState('serviceSubscription', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.serviceSubscription.search
      },
      set(newValue) {
        this.$store.commit('serviceSubscription/SET_SEARCH', newValue)
        this.$store.commit('serviceSubscription/RESET_CURRENT_PAGE')
      }
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    branch_id: {
      get() {
        return this.$store.state.serviceSubscription.branch_id
      },
      set(newValue) {
        this.$store.commit('serviceSubscription/SET_BRANCH_ID', newValue)
      }
    },
    columns() {
      return [
        { name: this.$t('serviceSubscription.branch') },
        { name: this.$t('serviceSubscription.cardNumber') },
        { name: this.$t('serviceSubscription.feeType') },
        { name: this.$t('serviceSubscription.feeName') },
        { name: this.$t('serviceSubscription.comment') },
        { name: this.$t('serviceSubscription.actionBy') },
        {
          name: this.$t('serviceSubscription.createdDate'),
          style: 'width:10%'
        },
        { name: this.$t('actions'), style: 'width:2%' }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch(
          'serviceSubscription/fetch',
          Object.assign(
            {
              branch_id: this.branch_id
            },
            { ...attributes }
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    getBranch() {
      this.$store
        .dispatch('user/getBranch', {
          filter: {
            user_branches: true
          }
        })
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
    },
    showForm() {
      this.$modal.show(
        FormComponent,
        {},
        {
          height: 'auto',
          width: '50%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.fetchData
        }
      )
    },
    showUnsubscribeModal(record) {
      this.unsubscribeModal = {
        visible: true,
        loading: false,
        record,
        unsubsDate: moment().format('DD-MM-YYYY'),
        unsubsReason: '',
        errors: {}
      }
    },
    deleteRecord() {
      let errors = {}

      if (!this.unsubscribeModal.unsubsDate) {
        errors.unsubsDate = this.$t(
          'serviceSubscription.unsubscribeDateRequired'
        )
      }

      if (!this.unsubscribeModal.unsubsReason) {
        errors.unsubsReason = this.$t(
          'serviceSubscription.unsubscribeReasonRequired'
        )
      }

      if (Object.keys(errors).length) {
        this.unsubscribeModal.errors = errors
        return
      }

      let record = this.unsubscribeModal.record

      record._deleting = true
      this.unsubscribeModal.loading = true
      this.$store
        .dispatch('serviceSubscription/destroy', {
          studentId: record.student_id,
          feeSetupId: record.fee_setup_id,
          unsubsDate: this.unsubscribeModal.unsubsDate,
          unsubsReason: this.unsubscribeModal.unsubsReason
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.unsubscribeModal.visible = false
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
          this.unsubscribeModal.loading = false
        })
    },
    getActionUserName(record) {
      if (!record.action_user) {
        return ''
      }

      return record.action_user.employee
        ? record.action_user.employee.name_en
        : record.action_user.loginname
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search, branch_id: this.branch_id })
    }, 500),
    is_external: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('serviceSubscription/RESET_STATE')
    next()
  }
}
</script>
